import React, { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { EditIconButton } from "components"
import Search from "antd/es/input/Search"
import { Button, Checkbox, Select, Table, Tag } from "antd"
import InfiniteScroll from "components/InfiniteScroll"
import { SOURCE_GAME_OPTIONS, SourceGameEnum, SourceGameType } from "domains/accountsBuffPlay/constants/source-options"
import { NativeGameDetailsModal } from "./native-game-details-modal"
import { nativeGamesControlColumns } from "./columns"
import { ColumnsType } from "antd/lib/table"
import { NativeGame } from "../store/types"
import { AddNativeGameModal } from "./add-native-game-modal"
import { useAdminUnlock } from "../hooks/useAdminUnlock"
import { AdminUnlockModal } from "./admin-unlock-modal"

export const NativeGamesControl: FC = observer<FC>(() => {
  const { dataStore: { nativeGamesControlStore } } = useStore()
  const [selectedGame, setSelectedGame] = useState<NativeGame | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [showUnlockModal, setShowUnlockModal] = useState(false)
  const [unlockTarget, setUnlockTarget] = useState<"addGame" | null>(null)
  const { unlocked, verifyPassword } = useAdminUnlock()

  const handleAddGameClick = () => {
    if (!unlocked) {
      setUnlockTarget("addGame")
      setShowUnlockModal(true)
    } else {
      setIsAddModalOpen(true)
    }
  }

  const handlePasswordVerify = (password: string) => {
    if (verifyPassword(password)) {
      setShowUnlockModal(false)
      if (unlockTarget === "addGame") {
        setIsAddModalOpen(true)
        setUnlockTarget(null)
      }
    }
  }

  // The store doesn't pre-seed filter (to avoid an eager boot fetch),
  // so spread off a fallback object — undefined would throw.
  const currentFilter = nativeGamesControlStore.filter ?? {}

  const searchNameHandler = async(value: string) => {
    nativeGamesControlStore.applyFilter({ ...currentFilter, name: value })
  }

  const sourceHandler = (value: SourceGameType) => {
    nativeGamesControlStore.applyFilter({ ...currentFilter, gameSource: value })
  }

  const hideDeletedHandler = (checked: boolean) => {
    nativeGamesControlStore.applyFilter({ ...currentFilter, hideDeleted: checked })
  }

  const columns: ColumnsType<NativeGame> = [
    ...nativeGamesControlColumns,
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record: NativeGame) => {
        const anyInstallable = (record.platforms ?? []).some(p => p.isInstallable)
        const allInstallable = (record.platforms ?? []).every(p => p.isInstallable)

        let status = "Active"
        let color = "green"

        if (record.isDeleted) {
          status = "Deleted"
          color = "red"
        } else if (!anyInstallable) {
          status = "Paused"
          color = "gray"
        } else if (!allInstallable) {
          status = "Partially Paused"
          color = "gold"
        }

        return <Tag color={color}>{status}</Tag>
      },
    },
    {
      title: "Update Game",
      dataIndex: "update",
      key: "update",
      render: (_, data) => (
        <EditIconButton
          onClick={() => {
            setSelectedGame(data)
            if (!unlocked) {
              setShowUnlockModal(true)
            }
          }}
        />
      ),
    },
  ]

  useEffect(() => {
    nativeGamesControlStore.getData().then()
  }, [nativeGamesControlStore])

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", height: "40px", justifyContent: "space-between", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          Filters:
          <Search
            style={{ width: "500px" }}
            placeholder="Name"
            enterButton
            onSearch={searchNameHandler}
          />
          <div>Source:</div>
          <Select<SourceGameType>
            style={{ width: "140px" }}
            value={currentFilter.gameSource ?? SourceGameEnum.NATIVE}
            onChange={sourceHandler}
          >
            {SOURCE_GAME_OPTIONS.map(option => (
              <Select.Option value={option} key={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
          <Checkbox
            checked={currentFilter.hideDeleted ?? true}
            onChange={e => hideDeletedHandler(e.target.checked)}
          >
            Hide deleted
          </Checkbox>
        </div>

        <Button
          type="primary"
          onClick={handleAddGameClick}
        >
          Add Native Game
        </Button>
      </div>

      <Table
        dataSource={nativeGamesControlStore.nativeGames || []}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={nativeGamesControlStore.isLoading}
      />
      <InfiniteScroll
        onLoadMore={() => {
          nativeGamesControlStore.loadedPages += 1
          nativeGamesControlStore.getData()
        }}
        isLoading={nativeGamesControlStore.isLoading}
        hasMore={
          nativeGamesControlStore.nativeGames?.length < nativeGamesControlStore.total
        }
      />
      {selectedGame && unlocked && (
        <NativeGameDetailsModal
          selectedGame={selectedGame}
          setSelectedGame={setSelectedGame}
        />
      )}
      <AdminUnlockModal
        open={showUnlockModal && !unlocked}
        onVerify={handlePasswordVerify}
        onCancel={() => {
          setShowUnlockModal(false)
          setUnlockTarget(null)
          setSelectedGame(null)
        }}
      />
      <AddNativeGameModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </>
  )
})

